/**
 * @file DealsBoardMobile.tsx
 * @description Доска сделок на телефоне и планшете (до 1024; phase19.31):
 * стадии — пилюлями со счётчиками сверху, под ними список сделок одной
 * выбранной стадии — одна колонка на экран.
 *
 * Пилюли переносятся по строкам (`FilterChips`), поэтому страница не
 * прокручивается по горизонтали. Перетаскивать пальцем по длинным колонкам
 * неудобно: стадию меняют в карточке сделки. Строка — ссылка 72px:
 * «название … сумма» / «клиент» / «ответственный · просрочка».
 */

import { useState } from "react";
import { Link } from "react-router";

import { OverdueMark } from "../deals/DealDeadline";
import { FilterChips } from "../../ui/FilterChips";
import { LIST_FRAME_CLASS } from "../../ui/ListStates";
import type { DealDto, PipelineStageDto } from "../../../lib/api-crm";
import { dealDeadline, formatRub } from "../../../lib/crm-deal-view";
import { cn } from "@/lib/utils";

interface Props {
  stages: PipelineStageDto[];
  deals: DealDto[];
  /** Адрес карточки сделки. */
  dealHref: (deal: DealDto) => string;
}

const ROW_CLASS =
  "flex min-h-18 flex-col justify-center gap-0.5 px-3 py-2 outline-none transition-colors hover:bg-[var(--row-hover)] focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring";

export function DealsBoardMobile({ stages, deals, dealHref }: Props) {
  const [picked, setPicked] = useState<string | null>(null);
  const stageId = picked && stages.some((s) => s.id === picked) ? picked : (stages[0]?.id ?? "");
  const stageDeals = deals.filter((d) => d.currentStageId === stageId);
  const count = (id: string) => deals.filter((d) => d.currentStageId === id).length;

  return (
    <div className="flex min-w-0 flex-col gap-3">
      <FilterChips
        aria-label="Стадия"
        items={stages.map((s) => ({ value: s.id, label: s.name, count: count(s.id) }))}
        value={stageId}
        onChange={setPicked}
      />
      {stageDeals.length === 0 ? (
        <p className="type-body rounded-xl bg-surface-lowest px-4 py-6 text-center text-on-surface-variant">
          На этой стадии сейчас пусто
        </p>
      ) : (
        <ul className={cn(LIST_FRAME_CLASS, "divide-y divide-[var(--border)]")}>
          {stageDeals.map((d) => {
            const deadline = dealDeadline(d);
            return (
              <li key={d.id}>
                <Link to={dealHref(d)} className={ROW_CLASS}>
                  <span className="flex items-center justify-between gap-2">
                    <span className="type-body min-w-0 truncate font-medium">{d.displayName}</span>
                    <span className="type-body whitespace-nowrap font-semibold tabular-nums">
                      {formatRub(d.expectedAmountGross, "whole")}
                    </span>
                  </span>
                  <span className="type-caption truncate text-on-surface-variant">{d.customerDisplayName ?? "Клиент не указан"}</span>
                  <span className="type-caption flex min-w-0 items-center gap-2 text-on-surface-variant">
                    <span className="truncate">{d.ownerDisplayName ?? "не назначен"}</span>
                    {deadline.kind === "overdue" ? <OverdueMark text={deadline.overdue} /> : null}
                  </span>
                </Link>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
